import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Dialog, DialogContent, TextField, Typography, DialogActions,
  DialogTitle, Grid, CircularProgress, Alert, Box, Stack, MenuItem,
  Select, InputLabel, FormControl
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { AccountCircle } from '@mui/icons-material';

// Tus hooks y constantes
import { useMeCore } from '../../core/modules/me';
import { useGlobalCore } from '../../core/modules/global';
import { ME_USER_DIALOG } from '../../core/constants/dialog';
import { COUNTRY } from '../../core/constants/country';
import { PutMeUserPayloadRequest } from '../../core/modules/me/interfaces/put-me-user';

// 1. Interfaz de datos del formulario alineada con la API
interface FormData {
  fullName: string;
  phoneNumber?: string;
  country?: string;
  city?: string;
}
// 2. Esquema de validación simplificado
const schema = yup.object().shape({
  fullName: yup.string().required('Debes ingresar tu nombre completo.'),
  phoneNumber: yup.string(),
  country: yup.string(),
  city: yup.string(),
});

function UserDialog() {
  const { t } = useTranslation();
  const { dialog, closeDialog, showSnackbar } = useGlobalCore();
  const { user, putMeUser, getMeUser } = useMeCore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, isDirty, errors } = formState;

  // 3. Poblar el formulario con `reset` cuando el usuario carga o el diálogo se abre
  useEffect(() => {
    if (user && dialog === ME_USER_DIALOG) {
      reset({
        fullName: user.full_name || '',
        phoneNumber: user.phone_number || '',
        country: user.country || '',
        city: user.city || '',
      });
    }
  }, [user, dialog, reset]);

  const handleClose = () => {
    setApiError(null);
    closeDialog();
  };

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (formData) => {
      setIsSubmitting(true);
      setApiError(null);

      const payload: PutMeUserPayloadRequest = {
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        country: formData.country,
        city: formData.city,
      };

      await putMeUser(payload,
        () => { // onSuccess
          showSnackbar({ message: 'Perfil actualizado con éxito', options: { variant: 'success' } });
          getMeUser(); // Recargar los datos del usuario para verlos actualizados en toda la app
          handleClose();
        },
        (error: any) => { // onFailure
          const message = error.response?.data?.detail || 'Ocurrió un error al actualizar.';
          setApiError(message);
        }
      );
      
      setIsSubmitting(false);
    },
    [putMeUser, getMeUser, showSnackbar, handleClose]
  );

  return (
    <Dialog open={dialog === ME_USER_DIALOG} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h5" component="div" className="font-bold flex items-center gap-2">
          <AccountCircle />
          Editar Mi Perfil
        </Typography>
      </DialogTitle>
      
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2} className="pt-2">
            {apiError && <Alert severity="error">{apiError}</Alert>}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller name="fullName" control={control} render={({ field }) => (
                  <TextField {...field} label="Nombre Completo" error={!!errors.fullName} helperText={errors.fullName?.message} fullWidth required autoFocus />
                )} />
              </Grid>
              <Grid item xs={12}>
                <Controller name="phoneNumber" control={control} render={({ field }) => (
                  <TextField {...field} label="Teléfono" error={!!errors.phoneNumber} helperText={errors.phoneNumber?.message} fullWidth />
                )} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="country-select-label">{t('label.country')}</InputLabel>
                  <Controller name="country" control={control} render={({ field }) => (
                    <Select {...field} labelId="country-select-label" label={t('label.country')}>
                      <MenuItem value=""><em>Ninguno</em></MenuItem>
                      {COUNTRY.map((c) => <MenuItem value={c.name} key={c.code}>{c.name}</MenuItem>)}
                    </Select>
                  )} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="city" control={control} render={({ field }) => (
                  <TextField {...field} label={t('label.city')} error={!!errors.city} helperText={errors.city?.message} fullWidth />
                )} />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={handleClose} disabled={isSubmitting}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isDirty || !isValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default UserDialog;